import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Radio, Moon } from "lucide-react";

/**
 * Devices ↔ Patterns correlation strip for The Brief.
 *
 * Renders a single deadpan headline (deterministically picked — the
 * strongest device × phase skew) plus two side-by-side heatmaps:
 * device rows × lunar columns, device rows × circadian columns.
 *
 * Loads /api/sight/patterns once on mount. Quietly renders nothing
 * when there are no device events — the Brief should not look
 * broken on a fresh user. Iter 27, Feb 2026.
 */
export default function PatternsStrip() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        let cancelled = false;
        // Send local TZ offset so circadian buckets are local-time aware.
        const tz = -new Date().getTimezoneOffset() / 60;
        const t0 = Date.now();
        (async () => {
            try {
                const { data } = await api.get(`/sight/patterns?tz_offset_hours=${tz}`);
                if (!cancelled) setData(data);
            } catch (e) {
                const msg = e?.response?.data?.detail || e.message || "Patterns failed";
                if (!cancelled) setErr(msg);
                // Iter 31.42 — file silent 504/timeout/Network failures
                // to /incidents so post-mortem has the data.
                try {
                    await api.post("/admin/incident-log", {
                        kind: "patterns_strip_failed",
                        message: msg,
                        path: "/brief",
                        context: {
                            fired_at: new Date().toISOString(),
                            elapsed_ms: Date.now() - t0,
                            http_status: e?.response?.status || null,
                            tz_offset_hours: tz,
                        },
                        user_agent: navigator.userAgent || "",
                    });
                } catch (_) { /* incident log itself failed — nothing more we can do */ }
            } finally {
                if (!cancelled) setBusy(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (busy) {
        return (
            <div className="paper-card border border-dashed border-ink/20 p-4 mb-6" data-testid="patterns-strip-busy">
                <div className="label-typed text-ink-soft">Reading the wall calendar…</div>
            </div>
        );
    }
    if (err || !data || data.total_device_events === 0) {
        // Stay quiet — no devices, no strip. Errors render compact.
        return err ? (
            <div className="paper-card border border-stamp-red/40 p-3 mb-6 font-body text-xs text-stamp-red" data-testid="patterns-strip-error">
                Patterns offline: {err}
            </div>
        ) : null;
    }

    return (
        <section
            className="paper-card border border-ink/25 p-5 mb-8 relative"
            data-testid="patterns-strip"
        >
            <div className="absolute top-3 right-4 classified-stamp">DEVICES × PHASE</div>
            <div className="flex items-center gap-2 mb-3">
                <Radio className="w-4 h-4 text-stamp-red" strokeWidth={1.4} />
                <div className="font-heading uppercase tracking-[0.2em] text-sm text-ink">
                    Devices ↔ Patterns
                </div>
                <div className="font-body text-[10px] text-ink-soft uppercase tracking-[0.18em] ml-2">
                    {data.total_device_events} signals · {data.device_count} devices
                </div>
            </div>

            {data.headline && (
                <div
                    className="font-body text-sm text-ink leading-snug mb-4 pl-3 border-l-2 border-stamp-red"
                    data-testid="patterns-headline"
                >
                    <span className="font-heading uppercase tracking-[0.15em] text-[10px] text-stamp-red mr-2">
                        Habit
                    </span>
                    {data.headline.headline}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <Heatmap
                    title="Lunar"
                    icon={<Moon className="w-3 h-3" />}
                    rows={data.lunar.rows}
                    order={data.lunar.order}
                    formatColLabel={lunarShort}
                    testid="patterns-heatmap-lunar"
                />
                <Heatmap
                    title="Circadian"
                    icon={<span className="text-stamp-red leading-none">◐</span>}
                    rows={data.circadian.rows}
                    order={data.circadian.order}
                    formatColLabel={circadianShort}
                    testid="patterns-heatmap-circadian"
                />
            </div>
        </section>
    );
}

function lunarShort(label) {
    return {
        "new": "NEW",
        "waxing-crescent": "WX·C",
        "first-quarter": "1Q",
        "waxing-gibbous": "WX·G",
        "full": "FULL",
        "waning-gibbous": "WN·G",
        "last-quarter": "L·Q",
        "waning-crescent": "WN·C",
    }[label] || label;
}

function circadianShort(label) {
    return {
        "dead-of-night": "DEAD",
        "predawn": "PRE",
        "morning": "MORN",
        "late-morning": "L·MORN",
        "midday": "MID",
        "afternoon": "AFT",
        "evening": "EVE",
        "night": "NIGHT",
    }[label] || label;
}

function Heatmap({ title, icon, rows, order, formatColLabel, testid }) {
    if (!rows || rows.length === 0) {
        return (
            <div className="border border-dashed border-ink/20 p-4" data-testid={`${testid}-empty`}>
                <div className="label-typed">{title} · no device events yet</div>
            </div>
        );
    }
    // global max across all cells for intensity normalisation
    let max = 0;
    for (const r of rows) {
        for (const k of order) {
            max = Math.max(max, r.counts?.[k] || 0);
        }
    }
    return (
        <div data-testid={testid}>
            <div className="label-typed mb-2 flex items-center gap-2">
                {icon} {title}
            </div>
            <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[10px] font-body">
                    <thead>
                        <tr>
                            <th className="text-left text-ink-soft uppercase tracking-[0.15em] py-1 pr-2">Device</th>
                            {order.map((col) => (
                                <th
                                    key={col}
                                    className="text-center text-ink-soft uppercase tracking-[0.12em] px-1 py-1"
                                    title={col}
                                >
                                    {formatColLabel(col)}
                                </th>
                            ))}
                            <th className="text-right text-ink-soft uppercase tracking-[0.15em] py-1 pl-2">Σ</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.device_label} className="border-t border-ink/10">
                                <td
                                    className="font-heading uppercase tracking-[0.1em] text-ink py-1 pr-2 whitespace-nowrap"
                                    title={r.device_type}
                                >
                                    {r.device_label}
                                </td>
                                {order.map((col) => {
                                    const c = r.counts?.[col] || 0;
                                    const intensity = max > 0 ? c / max : 0;
                                    return (
                                        <td key={col} className="px-0.5 py-0.5">
                                            <div
                                                className={`text-center font-mono leading-tight border ${
                                                    c > 0 ? "border-stamp-red/40 text-ink" : "border-ink/10 text-ink-soft/40"
                                                }`}
                                                style={{
                                                    backgroundColor:
                                                        c > 0
                                                            ? `rgba(196, 30, 58, ${0.08 + intensity * 0.5})`
                                                            : "transparent",
                                                    padding: "4px 2px",
                                                    minWidth: "30px",
                                                }}
                                                data-testid={`${testid}-cell-${r.device_label}-${col}`}
                                            >
                                                {c || "·"}
                                            </div>
                                        </td>
                                    );
                                })}
                                <td className="text-right font-heading text-ink py-1 pl-2">{r.total}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
